import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, PhoneMissed, Zap, BellRing } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { formatDuree } from "@/components/poste/ResultatAppel";
import { MesRetours } from "@/components/tableaux/Retours";

const heure = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const Jauge = ({ label, valeur, objectif, testId }) => {
  const pct = objectif ? Math.min(100, Math.round((valeur / objectif) * 100)) : 0;
  return (
    <div className="at-card p-4 min-w-0">
      <p className="at-label !tracking-normal !normal-case text-xs truncate">{label}</p>
      <p className="text-2xl font-bold mt-1" data-testid={testId}>{valeur}<span className="text-sm at-muted font-semibold"> / {objectif}</span></p>
      <div className="h-2 at-pill mt-2 overflow-hidden" style={{ background: "var(--surface-2)" }}><span className="block h-full at-pill" style={{ width: `${pct}%`, background: pct >= 100 ? "var(--success)" : "var(--accent)", transition: "width .5s ease" }} /></div>
    </div>
  );
};

const Manques = ({ liste, appeler }) => liste.length > 0 && (
  <section className="at-card p-4 space-y-2" style={{ background: "color-mix(in srgb, var(--danger) 10%, var(--surface))" }} data-testid="missed-calls">
    <p className="font-bold flex items-center gap-2" style={{ color: "var(--danger)" }}><PhoneMissed size={16} />{liste.length} appel(s) manqué(s)</p>
    {liste.map((m) => (
      <div key={m.id} className="flex items-center justify-between gap-2" data-testid={`missed-${m.id}`}>
        <div className="min-w-0"><p className="font-semibold truncate">{m.nom}</p><p className="text-xs at-muted">{m.numero} · {formatDateHeure(m.created_at)}</p></div>
        <button type="button" onClick={() => appeler(m.prospect_id)} className="at-btn at-btn-danger shrink-0 !px-3" data-testid={`missed-call-${m.id}`}><Phone size={15} />Rappeler</button>
      </div>
    ))}
  </section>
);

const Rappels = ({ liste, appeler }) => (
  <section className="at-card p-4 space-y-2" data-testid="today-callbacks">
    <p className="font-bold flex items-center gap-2"><BellRing size={16} />Rappels du jour ({liste.length})</p>
    {liste.length === 0 && <p className="text-sm at-muted" data-testid="today-callbacks-empty">Aucun rappel prévu aujourd'hui.</p>}
    {liste.map((t) => (
      <div key={t.id} className="flex items-center justify-between gap-2" data-testid={`callback-row-${t.id}`}>
        <div className="min-w-0"><p className="font-semibold truncate"><b className="tabular-nums">{heure(t.echeance)}</b> · {t.titre}</p><p className="text-xs at-muted truncate">{t.note}</p></div>
        {t.prospect_id
          ? <button type="button" onClick={() => appeler(t.prospect_id)} className="at-icon-btn shrink-0" aria-label={`Appeler ${t.titre}`} data-testid={`callback-call-${t.id}`}><Phone size={16} /></button>
          : <a href={t.telephone ? `tel:${t.telephone.replace(/\s/g, "")}` : `/fiches/${t.fiche_id}`} className="at-icon-btn shrink-0" aria-label={`Appeler ${t.titre}`} data-testid={`callback-call-${t.id}`}><Phone size={16} /></a>}
      </div>
    ))}
  </section>
);

const Statuts = ({ liste }) => {
  const total = liste.reduce((s, x) => s + x.nombre, 0);
  return (
    <section className="at-card p-4 space-y-3" data-testid="cumulative-statuses">
      <p className="font-bold">Mes statuts, tous fichiers ({total})</p>
      <div className="flex h-3 at-pill overflow-hidden" style={{ background: "var(--surface-2)" }}>{liste.map((s) => <span key={s.code} style={{ flex: s.nombre, background: s.couleur }} title={`${s.nom} : ${s.nombre}`} />)}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        {liste.map((s) => <p key={s.code} className="flex items-center gap-2 min-w-0" data-testid={`cumul-${s.code}`}><span className="w-2.5 h-2.5 at-pill shrink-0" style={{ background: s.couleur }} /><span className="truncate flex-1">{s.nom}</span><b>{s.nombre}</b></p>)}
      </div>
    </section>
  );
};

export const PosteAgent = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const charger = useCallback(() => api.get("/poste/accueil").then((r) => setD(r.data)).catch((e) => toast.error(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const appeler = (pid) => navigate(`/focus?prospect=${pid}`);
  const simuler = () => api.post("/poste/simuler-appel-manque", {}).then((r) => { toast(`Appel manqué de ${r.data.nom} (simulation).`); charger(); }).catch((e) => toast.error(messageErreur(e)));
  if (!d) return <p className="at-muted" data-testid="poste-loading">Chargement de votre poste…</p>;
  return (
    <div className="space-y-3 mb-8 min-w-0" data-testid="agent-station">
      <MesRetours />
      <div className="grid grid-cols-3 gap-2" data-testid="daily-counter">
        <Jauge label="Appels" valeur={d.compteur.appels} objectif={d.objectifs.objectif_appels} testId="counter-calls" />
        <Jauge label="Décrochés" valeur={d.compteur.decroches} objectif={d.objectifs.objectif_decroches} testId="counter-answered" />
        <Jauge label="Opportunités" valeur={d.compteur.opportunites} objectif={d.objectifs.objectif_opportunites} testId="counter-opportunities" />
      </div>
      <Manques liste={d.manques} appeler={appeler} />
      {d.annulations?.length > 0 && (
        <section className="at-card p-4 space-y-2" style={{ background: "color-mix(in srgb, var(--temp-3) 12%, var(--surface))" }} data-testid="cancelled-files">
          <p className="font-bold">Dossiers annulés par la confirmation ({d.annulations.length})</p>
          {d.annulations.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-2" data-testid={`cancelled-${a.id}`}>
              <Link to={`/fiches/${a.fiche_id}`} className="min-w-0 min-h-[44px] flex flex-col justify-center"><span className="font-semibold truncate">{a.fiche_nom}</span><span className="text-xs at-muted truncate">{a.motif}{a.precision ? ` · ${a.precision}` : ""} · par {a.par}</span></Link>
              <button type="button" onClick={() => api.post(`/poste/annulations/${a.id}/vu`).then(charger)} className="at-btn at-btn-ghost shrink-0 !px-3 min-w-[48px] text-sm" data-testid={`cancelled-seen-${a.id}`}>Vu</button>
            </div>
          ))}
        </section>
      )}
      <Link to="/focus" className="at-btn at-btn-primary w-full !min-h-[56px] text-base" data-testid="next-contact-btn"><Zap size={18} />Prochaine fiche</Link>
      <Rappels liste={d.rappels} appeler={appeler} />
      {d.statuts.length > 0 && <Statuts liste={d.statuts} />}
      {d.derniers_appels.length > 0 && (
        <section className="at-card p-4 space-y-1" data-testid="recent-calls">
          <p className="font-bold mb-1">Derniers appels</p>
          {d.derniers_appels.map((a) => <p key={a.id} className="text-sm flex justify-between gap-2"><span className="truncate">{heure(a.created_at)} · {a.nom}</span><span className="at-muted shrink-0">{a.resultat_nom || (a.decroche ? "Décroché" : "Sans réponse")} · {formatDuree(a.duree_s)}</span></p>)}
        </section>
      )}
      <button type="button" onClick={simuler} className="at-btn at-btn-ghost w-full text-sm" data-testid="simulate-missed-call">Simuler un appel entrant manqué</button>
    </div>
  );
};
